/**
 * Notification Bell Component
 *
 * Bell icon button with badge showing unread count. Opens a dropdown panel
 * merging the server's persisted notifications (`GET /notifications`) with
 * any socket-pushed items still only in the local store, deduped by id.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { useNotifications, type Notification } from '@/stores/notificationStore';
import { useSocketIO } from '@/hooks/useSocketIO';
import { SocketIOEvent } from '@/lib/socketio';
import {
  useNotificationsList,
  useUnreadNotificationCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '@/hooks/queries';
import type { NotificationServerType, ServerNotification } from '@/types/notification';
import { NotificationPanel } from './NotificationPanel';

const LIST_PARAMS = { pageNumber: 1, pageSize: 20 };

function mapServerType(type: NotificationServerType): Notification['type'] {
  switch (type) {
    case 'Resource':
      return 'resource';
    case 'Billing':
      return 'billing';
    default:
      return 'system';
  }
}

function toPanelNotification(notification: ServerNotification): Notification {
  return {
    id: notification.id,
    type: mapServerType(notification.type),
    title: notification.title,
    message: notification.message,
    timestamp: notification.createdAt,
    read: notification.isRead,
    actionUrl: notification.actionUrl ?? undefined,
    metadata: notification.metadata ?? undefined,
    source: 'server',
  };
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const {
    notifications: localNotifications,
    addNotification,
    markAsRead: markLocalAsRead,
    markAllAsRead: markAllLocalAsRead,
  } = useNotifications();

  const { subscribe, isConnected } = useSocketIO();

  const { data: unreadCountData, refetch: refetchUnreadCount } = useUnreadNotificationCount();
  const { data: listData, refetch: refetchList } = useNotificationsList(LIST_PARAMS);
  const markServerRead = useMarkNotificationRead();
  const markAllServerRead = useMarkAllNotificationsRead();

  const serverNotifications = useMemo(
    () => listData?.data ?? [],
    [listData],
  );
  const serverIds = useMemo(
    () => new Set(serverNotifications.map((n) => n.id)),
    [serverNotifications],
  );

  // Socket-pushed items not yet reflected in the server list — kept on top,
  // ahead of the server's own newest-first order.
  const localOnly = useMemo(
    () => localNotifications.filter((n) => !serverIds.has(n.id)),
    [localNotifications, serverIds],
  );

  const combinedNotifications = useMemo(
    () => [...localOnly, ...serverNotifications.map(toPanelNotification)],
    [localOnly, serverNotifications],
  );

  const unreadCount =
    (unreadCountData?.count ?? 0) + localOnly.filter((n) => !n.read).length;

  // Listen for real-time notifications
  useEffect(() => {
    if (!isConnected) return;

    const handleNotification = (notification: unknown) => {
      const notificationData = notification as {
        id?: string;
        type?: string;
        title?: string;
        message?: string;
        timestamp?: string;
        actionUrl?: string;
        metadata?: Record<string, unknown>;
      };

      addNotification({
        id: notificationData.id || crypto.randomUUID(),
        type: (notificationData.type as any) || 'info',
        title: notificationData.title || 'Notification',
        message: notificationData.message || '',
        timestamp: notificationData.timestamp || new Date().toISOString(),
        read: false,
        actionUrl: notificationData.actionUrl,
        metadata: notificationData.metadata,
        source: 'local',
      });

      // Show toast for important notifications
      if (notificationData.type === 'error' || notificationData.type === 'warning') {
        toast.error(notificationData.title || 'Notification', {
          description: notificationData.message,
        });
      }
    };

    const unsubscribe = subscribe(SocketIOEvent.NotificationReceived, handleNotification);

    return () => {
      unsubscribe();
    };
  }, [subscribe, isConnected, addNotification]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      // Both, together: once the list returns a socket-pushed item it leaves the local count, so a
      // stale server count would make the badge drop although nothing was read.
      void refetchList();
      void refetchUnreadCount();
    }
  };

  const handleSelect = (notification: Notification) => {
    if (!notification.read) {
      if (notification.source === 'server') {
        markServerRead.mutate(notification.id);
      } else {
        markLocalAsRead(notification.id);
      }
    }

    if (notification.actionUrl) {
      setOpen(false);
      navigate(notification.actionUrl);
    }
  };

  const handleMarkAllAsRead = () => {
    markAllServerRead.mutate();
    markAllLocalAsRead();
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label="Notifications">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 min-w-[20px] flex items-center justify-center p-0 text-xs animate-pulse"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="p-0">
        <NotificationPanel
          notifications={combinedNotifications}
          onSelect={handleSelect}
          onMarkAllAsRead={handleMarkAllAsRead}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
